import { createServerFn } from "@tanstack/react-start"
import { requireAuth, requireOrgTokenToMatchOrgId, requireScopes, rateLimitFunction, createPostHogTrackingMiddleware } from "~/lib/middleware"
import { ApiResponse, ApiKey, apiKeySchema } from "~/lib/schemas"
import z from "zod"
import { LogtoScopes } from "~/lib/logto/logto"
import { ScopeRule } from "~/lib/scopes"

export const getApiKeysSchema = z.object({
  organization_id: z.string().min(1, 'Organization ID is required'),
  name: z.string().optional(), // Filter by API key name (partial match, case-insensitive)
  description: z.string().optional(), // Filter by API key description (partial match, case-insensitive)
  is_active: z.boolean().optional(), // Filter by active status
  scope: z.string().optional(), // Filter by specific scope
  sort_by: z.enum(['name', 'description', 'is_active', 'last_used_at', 'expire_at', 'created_at']).optional(),
  sort_order: z.enum(['asc', 'desc']).optional(),
})

export type GetApiKeysRequest = z.infer<typeof getApiKeysSchema>

export const getApiKeys = createServerFn({
  method: 'GET',
})
  .middleware([
    requireAuth,
    rateLimitFunction,
    requireScopes(ScopeRule.OR([LogtoScopes.AdminOrgKeysRead, ScopeRule.ORG(LogtoScopes.OrgKeyRead)])),
    requireOrgTokenToMatchOrgId,
  ])
  .validator(getApiKeysSchema)
  .handler(async (ctx) => {
    const { 
      organization_id, 
      name, 
      description, 
      is_active, 
      scope, 
      sort_by = 'created_at', 
      sort_order = 'desc' 
    } = ctx.data
    
    // Use real API
    const API_MANAGEMENT_BASE_URL = process.env.API_MANAGEMENT_BASE_URL || 'http://localhost:8000'
    const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY
    
    if (!BFF_API_KEY) {
      throw new Error('API_MANAGEMENT_API_KEY environment variable is required')
    }
    
    const params = new URLSearchParams({
      organization_id,
      ...(name && { name }),
      ...(description && { description }),
      ...(is_active !== undefined && { is_active: is_active.toString() }),
      ...(scope && { scope }),
      ...(sort_by && { sort_by }),
      ...(sort_order && { sort_order }),
    })
    
    
    const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/organizations/keys/info?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${BFF_API_KEY}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
    }
    
    const result = await response.json() as ApiResponse<ApiKey[]>
    return z.array(apiKeySchema).parse(result.data ?? [])
  }) 
